import type { SortDirection } from '../ui/Table';
import type { EnrichedIncident } from '../../data/types';

export type SortField = 'severity' | 'createdTime' | 'lastUpdated' | 'riskScore';
export type QueueSortField = SortField | 'queue';

export const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 };

// Generate a unique key for an incident
export function getIncidentKey(incident: EnrichedIncident): string {
  return `${incident.tenantId}:${incident.incidentId}`;
}

/**
 * Queue ordering shared by the desktop table and the mobile card list.
 * 'queue' surfaces open incidents with fresh alerts first, then by latest
 * activity, then by creation time.
 */
export function sortIncidents(
  incidents: EnrichedIncident[],
  sortField: QueueSortField,
  sortDirection: SortDirection
): EnrichedIncident[] {
  return [...incidents].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;

    switch (sortField) {
      case 'queue': {
        const aHasNewAlerts = a.platformStatus !== 'closed' && (a.newAlertCount ?? 0) > 0 ? 1 : 0;
        const bHasNewAlerts = b.platformStatus !== 'closed' && (b.newAlertCount ?? 0) > 0 ? 1 : 0;
        if (aHasNewAlerts !== bHasNewAlerts) {
          return (aHasNewAlerts - bHasNewAlerts) * multiplier;
        }

        const aActivityTime = new Date(a.newAlertsDetectedAt ?? a.lastUpdated ?? a.createdTime).getTime();
        const bActivityTime = new Date(b.newAlertsDetectedAt ?? b.lastUpdated ?? b.createdTime).getTime();
        if (aActivityTime !== bActivityTime) {
          return (aActivityTime - bActivityTime) * multiplier;
        }

        return (new Date(a.createdTime).getTime() - new Date(b.createdTime).getTime()) * multiplier;
      }
      case 'severity':
        return (severityOrder[a.severity] - severityOrder[b.severity]) * multiplier;
      case 'createdTime':
        return (new Date(a.createdTime).getTime() - new Date(b.createdTime).getTime()) * multiplier;
      case 'lastUpdated':
        return (new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime()) * multiplier;
      case 'riskScore':
        return ((a.aiEnrichment?.riskScore || 0) - (b.aiEnrichment?.riskScore || 0)) * multiplier;
      default:
        return 0;
    }
  });
}
